import React, { useEffect, useRef, useState } from 'react';
import { FormControlLabel, Grid, InputAdornment, Switch, TextField, Typography } from '@mui/material';
import { Package } from 'types/package';
import { PackageValidation } from '../validation/usePackageValidation';
import MoneyInput from 'components/masked-input/MoneyInput';
import { Supplier } from 'types/supplier';
import SuppliersAutoComplete from 'components/autocomplete/SuppliersAutoComplete';
import { RecordSection } from 'components/record-shell/RecordShell';
import { marginFromSalePrice } from 'helpers/margin';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface PackageFormProps {
  pkg: Package | null;
  handleChange: <K extends keyof Package>(index: K, value: Package[K]) => void;
  validation: PackageValidation;
}

function percent(cost: number, price: number) {
  return `${marginFromSalePrice(cost, price).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

const PackageForm: React.FC<PackageFormProps> = ({ pkg, handleChange, validation }) => {
  const [supplier, setSupplier] = useState<Supplier | null>(null);

  useEffect(() => {
    if (pkg?.supplier) {
      setSupplier(pkg.supplier);
    }
  }, [pkg]);

  const inputs = {
    name: useRef<HTMLInputElement>(null),
    supplier_id: useRef<HTMLInputElement>(null),
    cost_price: useRef<HTMLInputElement>(null),
    sale_price_min: useRef<HTMLInputElement>(null),
    sale_price_max: useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    const [key] = Object.keys(validation) as [keyof typeof inputs];

    if (!key) return;

    inputs[key].current?.focus();
  }, [validation]);

  const cost = Number(pkg?.cost_price) || 0;
  const min = Number(pkg?.sale_price_min) || 0;
  const max = Number(pkg?.sale_price_max) || 0;
  const showMargin = cost > 0 && (min > 0 || max > 0);

  return (
    <Grid container size={12} spacing={2} maxWidth={900}>
      <Grid size={12}>
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Identificação
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 7 }}>
        <TextField
          fullWidth
          inputRef={inputs.name}
          label="Nome do pacote"
          placeholder="Digite o nome do pacote"
          value={pkg?.name ?? ''}
          onChange={e => handleChange('name', e.target.value)}
          error={!!validation.name}
          helperText={validation.name}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <SuppliersAutoComplete
          label="Fornecedor"
          onSelect={supplier => {
            handleChange('supplier_id', supplier?.id ?? 0);
            handleChange('supplier', supplier);
            setSupplier(supplier);
          }}
          placeholder="Digite o nome do fornecedor"
          selected={supplier}
          ref={inputs.supplier_id}
          error={!!validation.supplier_id}
          helperText={validation.supplier_id}
        />
      </Grid>

      <Grid size={12}>
        <RecordSection title="Preços">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Custo"
                placeholder="Digite o custo"
                inputRef={inputs.cost_price}
                error={!!validation.cost_price}
                helperText={validation.cost_price}
                value={pkg?.cost_price || 0}
                onChange={e => handleChange('cost_price', Number(e.target.value))}
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    inputComponent: asInputComponent(MoneyInput),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Preço de venda mínimo"
                placeholder="Digite o preço de venda mínimo"
                value={pkg?.sale_price_min || 0}
                onChange={e => handleChange('sale_price_min', Number(e.target.value))}
                fullWidth
                inputRef={inputs.sale_price_min}
                error={!!validation.sale_price_min}
                helperText={validation.sale_price_min}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    inputComponent: asInputComponent(MoneyInput),
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Preço de venda máximo"
                placeholder="Digite o preço de venda máximo"
                value={pkg?.sale_price_max || 0}
                onChange={e => handleChange('sale_price_max', Number(e.target.value))}
                fullWidth
                inputRef={inputs.sale_price_max}
                error={!!validation.sale_price_max}
                helperText={validation.sale_price_max}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    inputComponent: asInputComponent(MoneyInput),
                  },
                }}
              />
            </Grid>

            {showMargin && (
              <Grid size={12}>
                <Typography variant="body2" color="textSecondary">
                  Margem sobre a venda:{' '}
                  {min > 0 && (
                    <>
                      <strong>{percent(cost, min)}</strong> na mínima
                    </>
                  )}
                  {min > 0 && max > 0 && ' e '}
                  {max > 0 && (
                    <>
                      <strong>{percent(cost, max)}</strong> na máxima
                    </>
                  )}
                </Typography>
              </Grid>
            )}
          </Grid>
        </RecordSection>
      </Grid>

      <Grid size={12}>
        <FormControlLabel
          label="Ativo"
          control={<Switch checked={!!pkg?.active} onChange={e => handleChange('active', e.target.checked ? 1 : 0)} />}
        />
      </Grid>
    </Grid>
  );
};

export default PackageForm;
